import { BusMapData, Seat, DensitySeat, Stop } from "../types/app.types";
import { StopNameFormatter } from "./stop_name_formatter";

export interface TransformedHeatmapData {
  matrix: number[][];
  tooltipData: string[][];
  indexToColor: Map<number, string>;
  segmentBoundaries: Set<number>[];
  shortenedStopNames: string[];
  stopNames: string[];
  segmentSums: any[][];
  generalAmounts: number[];
}

export class HeatmapTransformer {
  static transform(
    data: BusMapData,
    useMaxDensity: boolean
  ): TransformedHeatmapData {
    const stopNames = data.stops.map((stop: Stop) => stop.stop_name);

    const actualSeats =
      useMaxDensity && data.maxDensitySeatList
        ? data.maxDensitySeatList.length
        : data.statistics.totalSeats;

    const totalSeats = data.statistics.totalSeats;

    const { colorToIndex, indexToColor } = this.buildColorMaps(
      data,
      useMaxDensity
    );

    const matrix = this.createEmptyMatrix<number>(
      totalSeats,
      stopNames.length,
      0
    );
    const tooltipData = this.createEmptyMatrix<string>(
      totalSeats,
      stopNames.length,
      ""
    );
    const segmentBoundaries = Array(totalSeats)
      .fill(0)
      .map(() => new Set<number>());
    const segmentSums: any[][] = Array(totalSeats)
      .fill(0)
      .map(() => []);
    const generalAmounts = Array(totalSeats).fill(0);

    if (useMaxDensity && data.maxDensitySeatList) {
      this.fillMaxDensityData(
        data,
        stopNames,
        totalSeats,
        actualSeats,
        colorToIndex,
        matrix,
        tooltipData,
        segmentBoundaries,
        segmentSums,
        generalAmounts
      );
    } else if (data.seatList) {
      this.fillRegularSeatData(
        data,
        stopNames,
        totalSeats,
        colorToIndex,
        matrix,
        tooltipData,
        segmentBoundaries,
        segmentSums,
        generalAmounts
      );
    }

    return {
      matrix,
      tooltipData,
      indexToColor,
      segmentBoundaries,
      shortenedStopNames: StopNameFormatter.formatStopNames(stopNames),
      stopNames,
      segmentSums,
      generalAmounts,
    };
  }

  private static buildColorMaps(data: BusMapData, useMaxDensity: boolean) {
    const uniqueColors = new Set<string>();
    uniqueColors.add("#f1f3f4");

    if (useMaxDensity && data.maxDensitySeatList) {
      data.maxDensitySeatList.forEach((seat: DensitySeat) => {
        seat.segments.forEach((segment) => {
          if (segment.passenger?.agentColor) {
            uniqueColors.add(segment.passenger.agentColor);
          }
        });
      });
    } else if (data.seatList) {
      data.seatList.forEach((seat: Seat) => {
        if (seat.isOccupied && seat.passenger?.agentColor) {
          uniqueColors.add(seat.passenger.agentColor);
        }
      });
    }

    const colorToIndex = new Map<string, number>();
    const indexToColor = new Map<number, string>();
    let index = 0;

    uniqueColors.forEach((color) => {
      colorToIndex.set(color, index);
      indexToColor.set(index, color);
      index++;
    });

    return { colorToIndex, indexToColor };
  }

  private static createEmptyMatrix<T>(
    rows: number,
    cols: number,
    defaultValue: T
  ): T[][] {
    return Array(rows)
      .fill(null)
      .map(() => Array(cols).fill(defaultValue));
  }

  private static fillMaxDensityData(
    data: BusMapData,
    stopNames: string[],
    totalSeats: number,
    actualSeats: number,
    colorToIndex: Map<string, number>,
    matrix: number[][],
    tooltipData: string[][],
    segmentBoundaries: Set<number>[],
    segmentSums: any[][],
    generalAmounts: number[]
  ) {
    data.maxDensitySeatList!.forEach((seat: DensitySeat, seatIdx: number) => {
      if (seatIdx >= totalSeats) return;

      generalAmounts[seatIdx] = seat.generalAmount ?? 0;

      seat.segments.forEach((segment) => {
        const colorIndex =
          colorToIndex.get(segment.passenger.agentColor || "#f1f3f4") || 0;

        for (
          let stopIdx = segment.fromStopIndex;
          stopIdx < segment.toStopIndex;
          stopIdx++
        ) {
          if (stopIdx < stopNames.length) {
            matrix[seatIdx][stopIdx] = colorIndex;
            tooltipData[seatIdx][stopIdx] = this.buildMaxDensityTooltip(
              seatIdx,
              segment
            );
          }
        }

        if (
          segment.toStopIndex - 1 >= 0 &&
          segment.toStopIndex - 1 < stopNames.length
        ) {
          segmentBoundaries[seatIdx].add(segment.toStopIndex - 1);
        }

        segmentSums[seatIdx].push({
          startIdx: segment.fromStopIndex,
          endIdx: segment.toStopIndex,
          sum: segment.passenger.sum,
        });
      });

      for (let stopIdx = 0; stopIdx < stopNames.length; stopIdx++) {
        if (matrix[seatIdx][stopIdx] === 0) {
          tooltipData[seatIdx][stopIdx] = `Віртуальне місце: ${
            seatIdx + 1
          }\nЗупинка: ${stopNames[stopIdx]}\nСтатус: Вільне`;
        }
      }
    });

    for (let seatIdx = actualSeats; seatIdx < totalSeats; seatIdx++) {
      generalAmounts[seatIdx] = 0;
      for (let stopIdx = 0; stopIdx < stopNames.length; stopIdx++) {
        matrix[seatIdx][stopIdx] = 0;
        tooltipData[seatIdx][stopIdx] = `Вільне місце: ${
          seatIdx + 1
        }\nЗупинка: ${stopNames[stopIdx]}\nСтатус: Вільне`;
      }
    }
  }

  private static fillRegularSeatData(
    data: BusMapData,
    stopNames: string[],
    totalSeats: number,
    colorToIndex: Map<string, number>,
    matrix: number[][],
    tooltipData: string[][],
    segmentBoundaries: Set<number>[],
    segmentSums: any[][],
    generalAmounts: number[]
  ) {
    data.seatList!.forEach((seat: Seat, seatIdx: number) => {
      if (seatIdx >= totalSeats) return;

      generalAmounts[seatIdx] = seat.generalAmount ?? 0;

      if (seat.isOccupied && seat.passenger) {
        const fromIndex = stopNames.findIndex(
          (stop) => stop === seat.passenger!.fromName
        );
        const toIndex = stopNames.findIndex(
          (stop) => stop === seat.passenger!.toName
        );

        if (fromIndex !== -1 && toIndex !== -1 && toIndex > fromIndex) {
          const colorIndex =
            colorToIndex.get(seat.passenger.agentColor || "#f1f3f4") || 0;
          for (let i = fromIndex; i < toIndex; i++) {
            matrix[seatIdx][i] = colorIndex;
            tooltipData[seatIdx][i] = this.buildRegularTooltip(seatIdx, seat);
          }
          if (toIndex - 1 >= 0 && toIndex - 1 < stopNames.length) {
            segmentBoundaries[seatIdx].add(toIndex - 1);
          }

          segmentSums[seatIdx].push({
            startIdx: fromIndex,
            endIdx: toIndex,
            sum: seat.passenger.sum,
          });
        }
      }

      for (let stopIdx = 0; stopIdx < stopNames.length; stopIdx++) {
        if (matrix[seatIdx][stopIdx] === 0) {
          tooltipData[seatIdx][stopIdx] = `Місце: ${seatIdx + 1}\nЗупинка: ${
            stopNames[stopIdx]
          }\nСтатус: Вільне`;
        }
      }
    });
  }

  private static buildTooltip(
    seatIdx: number,
    passenger: any,
    isMaxDensity: boolean,
    segmentLength?: number
  ): string {
    const lines = [
      isMaxDensity
        ? `Віртуальне місце: ${seatIdx + 1}`
        : `Місце: ${seatIdx + 1}`,
    ];

    if (isMaxDensity && segmentLength !== undefined) {
      lines.push(`Сегмент: ${segmentLength}`);
    } else if (!isMaxDensity && passenger.fromName && passenger.toName) {
      lines.push(`Сегмент: ${passenger.fromName} - ${passenger.toName}`);
    }

    if (passenger.seller) lines.push(`Агент: ${passenger.seller}`);
    if (passenger.fio) lines.push(`Пасажир: ${passenger.fio}`);
    if (passenger.phone) lines.push(`Телефон: ${passenger.phone}`);

    if (isMaxDensity && passenger.fromName && passenger.toName) {
      lines.push(`Маршрут: ${passenger.fromName} → ${passenger.toName}`);
    }

    if (passenger.sum) lines.push(`Сума: ${passenger.sum}`);
    if (passenger.stat) lines.push(`Статус: ${passenger.stat}`);

    return lines.join("\n");
  }

  private static buildMaxDensityTooltip(seatIdx: number, segment: any): string {
    return this.buildTooltip(
      seatIdx,
      segment.passenger,
      true,
      segment.toStopIndex - segment.fromStopIndex
    );
  }

  private static buildRegularTooltip(seatIdx: number, seat: Seat): string {
    return this.buildTooltip(seatIdx, seat.passenger, false);
  }
}
